import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../shell/AppShell.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import WorkerRecordDrawer from "../workerRecord/WorkerRecordDrawer.jsx";
import { useBackofficeIdentity } from "../hierarchy/BackofficeIdentityContext.jsx";
import { useSosCasesList, SOS_CASES_DEFAULT_PAGE_SIZE } from "./useSosCasesList.js";
import { getSosCaseDetail, acknowledgeSosCase, resolveSosCase } from "./sosApi.js";
import "./sosCases.css";
import "../pages/states.css";
import Pagination from "../components/Pagination.jsx";
import RecordDrawerHeader from "../components/RecordDrawerHeader.jsx";

import PageSkeleton, { DetailSkeleton } from "../components/Skeleton.jsx";
const CRUMBS = ["Backoffice", "Safety", "SOS Cases"];
const DRAWER_TITLE_ID = "sos-drawer-title";
const SEARCH_DEBOUNCE_MS = 350;

const CASE_STATUS_OPTIONS = ["Open", "Acknowledged", "Resolved"];
const SOS_TYPE_OPTIONS = ["Emergency", "Medical", "Police", "EmployerProblem", "Lost", "Other"];
const SEVERITY_OPTIONS = ["Low", "Medium", "High"];

const SUBTITLE_TEXT =
  "จัดการเคสขอความช่วยเหลือจากแรงงาน พร้อมตำแหน่งล่าสุดและรายชื่อผู้ที่ต้องแจ้ง ระบบยังไม่แจ้งให้อัตโนมัติ ต้องติดต่อเอง";

const POLICY_LINES = [
  "SOS สร้าง/เชื่อม Support Case และบันทึก location snapshot",
  "Recipient records เตรียม Agent + buddy สูงสุด 5 คน",
  "ระบบยังไม่แจ้งให้อัตโนมัติ ต้องติดต่อเอง",
  "ไม่มี external emergency service integration หรือคำแนะนำทางการแพทย์/กฎหมายอัตโนมัติ",
];

const NO_SEND_BUTTON_NOTE = "ไม่มีปุ่มส่ง notification ในหน้านี้ — recipient เป็นเพียงบันทึกผู้ที่เตรียมไว้เท่านั้น";
const LOCATION_SNAPSHOT_NOTE = "ตำแหน่งนี้แสดงเพื่อบริบทฉุกเฉินเท่านั้น เป็นภาพรวม ณ เวลาที่บันทึกไว้ ไม่ใช่การติดตามพิกัดแบบเรียลไทม์";
const LINKED_CASE_NOTE = "เคสเดียวกันถูกเชื่อมโยงเมื่อกด SOS ซ้ำระหว่างที่เคสยังเปิดอยู่ — ไม่สร้างเคสใหม่และไม่เตรียม recipient ซ้ำ";

const ACKNOWLEDGE_NOTICE_LINES = ["การ acknowledge คือรับทราบเคส ยังไม่ปิดเคส", "ทุกการ acknowledge จะถูกบันทึก audit"];
const RESOLVE_WARNING_LINES = ["การ resolve ต้องมีเหตุผลและถูกบันทึก audit", "ไม่มีการลบเคส"];

const FOOTNOTE_TEXT =
  "หน้านี้ไม่มีปุ่มส่งการแจ้งเตือนถึงแรงงาน การรับเรื่องและการปิดเรื่องทุกครั้งถูกบันทึกพร้อมชื่อผู้ทำและเวลา";

const CASE_STATUS_BADGE_VARIANT = { Open: "warning", Acknowledged: "info", Resolved: "success" };
const SEVERITY_BADGE_VARIANT = { Low: "info", Medium: "warning", High: "danger" };

function caseStatusBadgeVariant(value) {
  return CASE_STATUS_BADGE_VARIANT[value] ?? "neutral";
}

function severityBadgeVariant(value) {
  return SEVERITY_BADGE_VARIANT[value] ?? "neutral";
}

function formatDateTimeTh(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const datePart = date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
  const timePart = date.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${datePart} · ${timePart} UTC`;
}

function formatCoordinates(latitude, longitude) {
  if (latitude === null || latitude === undefined || longitude === null || longitude === undefined) {
    return null;
  }
  return `${latitude}, ${longitude}`;
}

function describeListError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์เข้าถึงรายการเคส SOS นี้";
  }
  return "กรุณาลองรีเฟรชหน้านี้อีกครั้ง หากยังพบปัญหากรุณาติดต่อฝ่ายปฏิบัติการ";
}

function describeDetailError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ดูรายละเอียดเคส SOS นี้";
  }
  if (error?.status === 404) {
    return "ไม่พบเคส SOS นี้ อาจถูกลบหรือย้ายแล้ว";
  }
  return "ไม่สามารถโหลดรายละเอียดได้ กรุณาลองใหม่อีกครั้ง";
}

function describeWriteError(error, conflictFallback) {
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ทำรายการนี้ เฉพาะ SuperAdmin หรือ Agent เจ้าของงานเท่านั้น";
  }
  if (error?.status === 404) {
    return "ไม่พบเคส SOS นี้ อาจถูกลบหรือย้ายแล้ว";
  }
  if (error?.status === 409) {
    return conflictFallback;
  }
  if (error?.status === 400) {
    return "กรุณาตรวจสอบข้อมูลที่กรอกอีกครั้ง";
  }
  return "ไม่สามารถบันทึกรายการนี้ได้ กรุณาลองใหม่อีกครั้ง";
}

/**
 * /backoffice/sos/cases - the Backoffice SOS Cases screen (Prompt 125B,
 * built from the W60 reference). Entirely over the Prompt 124 SOS Cases
 * API: a paginated, scope-isolated list of dbo.WmxSupportCases rows
 * (CaseType = Sos) (GET .../sos/cases), a single-case detail with latest
 * SOS event/event timeline/prepared recipients/location snapshot
 * (GET .../sos/cases/{supportCaseId}), and acknowledge/resolve
 * (POST .../sos/cases/{supportCaseId}/acknowledge|resolve). The mobile
 * submit path (not part of this packet) is the only writer of new cases -
 * this screen only ever transitions an existing case's own CaseStatus.
 *
 * This screen never sends a real notification (notificationDeliveryStarted
 * is always rendered false with the backend's own fixed reserved-reason
 * string), never calls an external emergency-service integration, and
 * never runs a medical/legal advice engine - sosType is a plain worker-
 * picked category label, nothing more. There is no notification-send
 * button, no external-emergency-service action, and no advice panel
 * anywhere on this page. P144-R gave this screen its own real, dedicated
 * sidebar item ("SOS Cases", -> /backoffice/sos/cases) - previously it was
 * only reachable by direct URL, sharing a single ambiguous "SOS / Abnormal"
 * nav entry with the unrelated Missing Heartbeat screen (see navConfig.js's
 * own doc comment for the full history).
 */
export default function SosCasesPage() {
  const { data: identity } = useBackofficeIdentity();
  const primaryRole = identity?.primaryRole;

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [caseStatusFilter, setCaseStatusFilter] = useState("");
  const [sosTypeFilter, setSosTypeFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [superAgentOrgInput, setSuperAgentOrgInput] = useState("");
  const [plcOrgInput, setPlcOrgInput] = useState("");
  const [agentIdInput, setAgentIdInput] = useState("");
  const [employerIdInput, setEmployerIdInput] = useState("");
  const [workerIdInput, setWorkerIdInput] = useState("");
  const [page, setPage] = useState(1);
  // Clicking the row opens the ONE shared worker record; the row's own
  // action button keeps opening this screen's process detail.
  const [workerRecordId, setWorkerRecordId] = useState(null);

  const [selectedSupportCaseId, setSelectedSupportCaseId] = useState(null);
  const [drawerMode, setDrawerMode] = useState(null); // null | "detail" | "acknowledge" | "resolve"
  const [detailState, setDetailState] = useState({ status: "idle", data: null, error: null, forId: null });
  const [detailReloadToken, setDetailReloadToken] = useState(0);

  const [noteInput, setNoteInput] = useState("");
  const [acknowledgeState, setAcknowledgeState] = useState({ status: "idle", error: null });

  const [resolutionNoteInput, setResolutionNoteInput] = useState("");
  const [resolveValidationError, setResolveValidationError] = useState(null);
  const [resolveState, setResolveState] = useState({ status: "idle", error: null });

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, caseStatusFilter, sosTypeFilter, severityFilter, superAgentOrgInput, plcOrgInput, agentIdInput, employerIdInput, workerIdInput]);

  const filters = useMemo(
    () => ({
      page,
      pageSize: SOS_CASES_DEFAULT_PAGE_SIZE,
      search,
      caseStatus: caseStatusFilter,
      sosType: sosTypeFilter,
      severity: severityFilter,
      superAgentOrganizationId: superAgentOrgInput,
      plcOrganizationId: plcOrgInput,
      agentId: agentIdInput,
      employerId: employerIdInput,
      workerId: workerIdInput,
    }),
    [page, search, caseStatusFilter, sosTypeFilter, severityFilter, superAgentOrgInput, plcOrgInput, agentIdInput, employerIdInput, workerIdInput]
  );

  const { status: listStatus, data: listData, error: listError, refetch: refetchList } = useSosCasesList(filters);

  // Detail is fetched whenever selectedSupportCaseId changes (row/card
  // opened) - the acknowledge/resolve subviews reuse the same fetch, they
  // never issue a second GET just to switch views within the same open drawer.
  useEffect(() => {
    if (!selectedSupportCaseId) {
      setDetailState({ status: "idle", data: null, error: null, forId: null });
      return undefined;
    }

    let cancelled = false;
    setDetailState((previous) => ({
      status: "loading",
      data: previous.forId === selectedSupportCaseId ? previous.data : null,
      error: null,
      forId: selectedSupportCaseId,
    }));

    getSosCaseDetail(selectedSupportCaseId)
      .then((data) => {
        if (!cancelled) {
          setDetailState({ status: "ready", data, error: null, forId: selectedSupportCaseId });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setDetailState({ status: "error", data: null, error, forId: selectedSupportCaseId });
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- detailReloadToken intentionally re-triggers a fetch for the same selectedSupportCaseId
  }, [selectedSupportCaseId, detailReloadToken]);

  function openDetail(supportCaseId) {
    setSelectedSupportCaseId(supportCaseId);
    setDrawerMode("detail");
    setNoteInput("");
    setAcknowledgeState({ status: "idle", error: null });
    setResolutionNoteInput("");
    setResolveValidationError(null);
    setResolveState({ status: "idle", error: null });
  }

  function closeDrawer() {
    setDrawerMode(null);
    setSelectedSupportCaseId(null);
  }

  function handleRefresh() {
    refetchList();
    if (selectedSupportCaseId) {
      setDetailReloadToken((token) => token + 1);
    }
  }

  function handleAcknowledgeSubmit() {
    setAcknowledgeState({ status: "submitting", error: null });
    acknowledgeSosCase(selectedSupportCaseId, { note: noteInput })
      .then(() => {
        setAcknowledgeState({ status: "success", error: null });
        setDetailReloadToken((token) => token + 1);
        refetchList();
        setDrawerMode("detail");
      })
      .catch((error) => {
        setAcknowledgeState({ status: "error", error });
      });
  }

  function handleResolveSubmit() {
    if (!resolutionNoteInput.trim()) {
      setResolveValidationError("กรุณาระบุ resolutionNote ก่อนบันทึก");
      return;
    }
    setResolveValidationError(null);
    setResolveState({ status: "submitting", error: null });
    resolveSosCase(selectedSupportCaseId, { resolutionNote: resolutionNoteInput })
      .then(() => {
        setResolveState({ status: "success", error: null });
        setDetailReloadToken((token) => token + 1);
        refetchList();
        setDrawerMode("detail");
      })
      .catch((error) => {
        setResolveState({ status: "error", error });
      });
  }

  const items = listData?.items ?? [];
  const totalCount = listData?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / SOS_CASES_DEFAULT_PAGE_SIZE));
  const hasActiveFilters = Boolean(
    search ||
      caseStatusFilter ||
      sosTypeFilter ||
      severityFilter ||
      superAgentOrgInput ||
      plcOrgInput ||
      agentIdInput ||
      employerIdInput ||
      workerIdInput
  );

  const isInitialLoading = listStatus === "loading" && listData === null;
  const isInitialError = listStatus === "error" && listData === null;

  if (isInitialLoading) {
    return (
      <AppShell crumbs={CRUMBS}>
        <PageSkeleton variant="table" label="กำลังโหลดรายการเคส SOS..." />
    </AppShell>
    );
  }

  if (isInitialError) {
    return (
      <AppShell crumbs={CRUMBS}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่สามารถโหลดรายการเคส SOS ได้</h1>
          <p className="wx-state-body">{describeListError(listError)}</p>
          <button type="button" className="wx-button wx-button--primary" onClick={handleRefresh}>
            ลองใหม่อีกครั้ง
          </button>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-sos-page">
        <header className="wx-sos-head">
          <div className="wx-sos-title">
            <h1>SOS Cases</h1>
            <p>{SUBTITLE_TEXT}</p>
          </div>
          {primaryRole && (
            <span className="wx-sos-scope-pill">
              Scope <b>{primaryRole}</b>
            </span>
          )}
        </header>

        <div className="wx-sos-policy-banner" role="status">
          <div className="wx-sos-policy-banner-title">
            <span className="wx-sos-policy-dot" aria-hidden="true" />
            นโยบาย SOS Cases
          </div>
          <ul>
            {POLICY_LINES.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>

        {/* A row of five KPI tiles stood here, every one of them rendering the
            word "สงวนไว้" over a pill reading "รอ Summary API" - a whole band of
            the page, on every visit, reporting that we had not built a summary
            endpoint. No number was ever shown. The list below is the real
            content; it is now the first thing under the header. */}

        <div className="wx-sos-toolbar">
          <label className="wx-sos-search">
            <span className="wx-visually-hidden">ค้นหารหัสเคส, รหัสแรงงาน หรือชื่อแรงงาน</span>
            <input
              type="search"
              placeholder="ค้นหารหัสเคส, รหัสแรงงาน หรือชื่อแรงงาน"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </label>

          <div className="wx-sos-toolbar-actions">
            <button type="button" className="wx-button wx-button--ghost wx-sos-refresh-button" onClick={handleRefresh}>
              Refresh
            </button>
          </div>

          <div className="wx-sos-filters">
            <label className="wx-sos-filter">
              <span>Status</span>
              <select value={caseStatusFilter} onChange={(event) => setCaseStatusFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {CASE_STATUS_OPTIONS.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-sos-filter">
              <span>SOS Type</span>
              <select value={sosTypeFilter} onChange={(event) => setSosTypeFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {SOS_TYPE_OPTIONS.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-sos-filter">
              <span>Severity</span>
              <select value={severityFilter} onChange={(event) => setSeverityFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {SEVERITY_OPTIONS.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-sos-filter">
              <span>Super Agent Org</span>
              <input type="text" placeholder="รหัส (ไม่บังคับ)" value={superAgentOrgInput} onChange={(event) => setSuperAgentOrgInput(event.target.value)} />
            </label>

            <label className="wx-sos-filter">
              <span>PLC Org</span>
              <input type="text" placeholder="รหัส (ไม่บังคับ)" value={plcOrgInput} onChange={(event) => setPlcOrgInput(event.target.value)} />
            </label>

            <label className="wx-sos-filter">
              <span>Agent</span>
              <input type="text" placeholder="รหัส (ไม่บังคับ)" value={agentIdInput} onChange={(event) => setAgentIdInput(event.target.value)} />
            </label>

            <label className="wx-sos-filter">
              <span>นายจ้าง</span>
              <input type="text" placeholder="รหัส (ไม่บังคับ)" value={employerIdInput} onChange={(event) => setEmployerIdInput(event.target.value)} />
            </label>

            <label className="wx-sos-filter">
              <span>แรงงาน</span>
              <input type="text" placeholder="รหัส (ไม่บังคับ)" value={workerIdInput} onChange={(event) => setWorkerIdInput(event.target.value)} />
            </label>
          </div>
        </div>

        <section className="wx-panel wx-sos-list-panel">
          <div className="wx-sos-list-head">
            <h2 className="wx-panel-title">รายการเคส SOS</h2>
            <span className="wx-sos-list-count">
              {totalCount} รายการ{listStatus === "loading" && listData !== null ? " · กำลังโหลด..." : ""}
            </span>
          </div>

          {listStatus === "error" && listData !== null && (
            <p className="wx-sos-inline-error" role="alert">
              {describeListError(listError)}
            </p>
          )}

          {items.length === 0 ? (
            <p className="wx-empty-list">{hasActiveFilters ? "ไม่พบรายการที่ตรงกับตัวกรองหรือคำค้นหา" : "ยังไม่มีเคส SOS ในขอบเขตที่มองเห็น"}</p>
          ) : (
            <>
              <div className="wx-sos-table-wrap">
                <table className="wx-sos-table">
                  <colgroup>
                    {/* 14 cols for 14 columns, summing to 100. It had FIFTEEN, totalling 108%.
                        table-layout:fixed reads these in order and ignores
                        any extra, so a mismatched or short list sizes the
                        real columns off something that was never about
                        them. If a column is added or removed, change this
                        list too. */}
                    <col style={{ width: "8%" }} /><col style={{ width: "7%" }} /><col style={{ width: "8%" }} /><col style={{ width: "6%" }} />
                    <col style={{ width: "9%" }} /><col style={{ width: "7%" }} /><col style={{ width: "8%" }} /><col style={{ width: "8%" }} />
                    <col style={{ width: "7%" }} /><col style={{ width: "7%" }} /><col style={{ width: "6%" }} /><col style={{ width: "7%" }} />
                    <col style={{ width: "7%" }} /><col style={{ width: "5%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Case Code</th>
                      <th>Status</th>
                      <th>SOS Type</th>
                      <th>Severity</th>
                      <th>แรงงาน</th>
                      <th>Assignment</th>
                      <th>นายจ้าง</th>
                      <th>Agent</th>
                      <th>เปิดเคสเมื่อ (UTC)</th>
                      <th>เหตุการณ์ล่าสุด (UTC)</th>
                      <th>Location Source</th>
                      <th>พิกัด</th>
                      <th>Recipients</th>
                      <th aria-hidden="true">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.supportCaseId} className="wx-sos-row" onClick={() => setWorkerRecordId(item.workerId)}>
                        <td>
                          <span className="wx-sos-code">{item.caseCode ?? "-"}</span>
                        </td>
                        <td>
                          <span className={`wx-badge wx-badge--${caseStatusBadgeVariant(item.caseStatus)}`}>{item.caseStatus}</span>
                        </td>
                        <td>
                          <span className="wx-badge wx-badge--info">{item.sosType ?? "-"}</span>
                        </td>
                        <td>
                          <span className={`wx-badge wx-badge--${severityBadgeVariant(item.severity)}`}>{item.severity ?? "-"}</span>
                        </td>
                        <td>
                          <span className="wx-sos-cell-strong">{item.workerName ?? "-"}</span>
                          <span className="wx-sos-cell-sub">{item.workerCode ?? "-"}</span>
                        </td>
                        <td>
                          <span className="wx-sos-code">{item.assignmentCode ?? item.assignmentId ?? "-"}</span>
                        </td>
                        <td>{item.employerName ?? "-"}</td>
                        <td>
                          <span className="wx-sos-cell-strong">{item.agentName ?? "-"}</span>
                          <span className="wx-sos-cell-sub">{item.agentCode ?? "-"}</span>
                        </td>
                        <td>
                          <span className="wx-sos-cell-strong">{formatDateTimeTh(item.openedAtUtc) ?? "-"}</span>
                        </td>
                        <td>
                          <span className="wx-sos-cell-strong">{formatDateTimeTh(item.lastEventAtUtc) ?? "-"}</span>
                        </td>
                        <td>
                          <span className="wx-badge wx-badge--neutral">{item.locationSource ?? "-"}</span>
                        </td>
                        <td>{formatCoordinates(item.latitude, item.longitude) ?? <span className="wx-sos-reserved-text">-</span>}</td>
                        <td>{item.recipientCount}</td>
                        <td>
                          <button
                            type="button"
                            className="wx-sos-row-action"
                            onClick={(event) => {
                              event.stopPropagation();
                              openDetail(item.supportCaseId);
                            }}
                          >
                            เปิดรายละเอียด
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="wx-sos-cards">
                {items.map((item) => (
                  <SosCaseCard key={item.supportCaseId} item={item} onOpen={() => openDetail(item.supportCaseId)} />
                ))}
              </div>

              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </>
          )}
        </section>

        <p className="wx-sos-footnote">{FOOTNOTE_TEXT}</p>
      </div>

      <DetailDrawer
        open={drawerMode !== null}
        onClose={closeDrawer}
        labelledBy={DRAWER_TITLE_ID}
        selectableSelector="tr.wx-sos-row, .wx-sos-card, .wx-sos-refresh-button"
      >
        {drawerMode === "detail" && (
          <CaseDetailView
            titleId={DRAWER_TITLE_ID}
            state={detailState}
            onAcknowledge={() => setDrawerMode("acknowledge")}
            onResolve={() => setDrawerMode("resolve")}
          />
        )}
        {drawerMode === "acknowledge" && (
          <AcknowledgeDrawerView
            titleId={DRAWER_TITLE_ID}
            detail={detailState.data}
            note={noteInput}
            onNoteChange={setNoteInput}
            submitState={acknowledgeState}
            onSubmit={handleAcknowledgeSubmit}
            onBack={() => setDrawerMode("detail")}
          />
        )}
        {drawerMode === "resolve" && (
          <ResolveDrawerView
            titleId={DRAWER_TITLE_ID}
            detail={detailState.data}
            resolutionNote={resolutionNoteInput}
            onResolutionNoteChange={setResolutionNoteInput}
            validationError={resolveValidationError}
            submitState={resolveState}
            onSubmit={handleResolveSubmit}
            onBack={() => setDrawerMode("detail")}
          />
        )}
      </DetailDrawer>
      
        <WorkerRecordDrawer
        open={workerRecordId !== null}
        workerId={workerRecordId}
        onClose={() => setWorkerRecordId(null)}
        selectableSelector="tr.wx-sos-row, .wx-sos-card"
      />
    </AppShell>
  );
}

function SosCaseCard({ item, onOpen }) {
  return (
    <div className="wx-sos-card" onClick={onOpen}>
      <div className="wx-sos-card-top">
        <div>
          <strong>{item.caseCode ?? "-"}</strong>
          <span className="wx-sos-card-sub">
            {item.workerName ?? "-"} · {item.workerCode ?? "-"}
          </span>
        </div>
        <div className="wx-sos-card-tags">
          <span className={`wx-badge wx-badge--${severityBadgeVariant(item.severity)}`}>{item.severity ?? "-"}</span>
          <span className={`wx-badge wx-badge--${caseStatusBadgeVariant(item.caseStatus)}`}>{item.caseStatus}</span>
        </div>
      </div>
      <div className="wx-sos-card-grid">
        <div>
          <label>SOS Type</label>
          <strong>
            <span className="wx-badge wx-badge--info">{item.sosType ?? "-"}</span>
          </strong>
        </div>
        <div>
          <label>เปิดเคสเมื่อ</label>
          <strong>{formatDateTimeTh(item.openedAtUtc) ?? "-"}</strong>
        </div>
        <div>
          <label>Location Source</label>
          <strong>
            <span className="wx-badge wx-badge--neutral">{item.locationSource ?? "-"}</span>
          </strong>
        </div>
        <div>
          <label>Recipients</label>
          <strong>{item.recipientCount}</strong>
        </div>
      </div>
      <button
        type="button"
        className="wx-sos-row-action wx-sos-row-action--full"
        onClick={(event) => {
          event.stopPropagation();
          onOpen();
        }}
      >
        เปิดรายละเอียด
      </button>
    </div>
  );
}

function CaseDetailView({ titleId, state, onAcknowledge, onResolve }) {
  if (state.status === "loading" || state.status === "idle") {
    return (
      <DetailSkeleton label="กำลังโหลดรายละเอียด..." />
    );
  }

  if (state.status === "error") {
    return (
      <div className="wx-sos-drawer-state" role="alert">
        <h2 id={titleId} className="wx-state-title">
          ไม่สามารถโหลดรายละเอียดได้
        </h2>
        <p className="wx-state-body">{describeDetailError(state.error)}</p>
      </div>
    );
  }

  const detail = state.data;
  const nextActions = detail.nextActions ?? {};
  const timeline = detail.eventTimeline ?? [];
  const recipients = detail.recipients ?? [];
  const latestEvent = timeline[0] ?? null;

  return (
    <div className="wx-sos-drawer">
      {/* The worker this case is about, presented exactly as on Worker
          Profiles (components/RecordDrawerHeader.jsx). */}
      <RecordDrawerHeader
        titleId={titleId}
        avatarText={detail.workerName}
        title={detail.workerName ?? "-"}
        subtitle={detail.workerCode ?? "-"}
        badges={[
          { label: detail.severity ?? "-", variant: severityBadgeVariant(detail.severity) },
          { label: detail.caseStatus, variant: caseStatusBadgeVariant(detail.caseStatus) },
          { label: detail.sosType ?? "-", variant: "info" },
          { label: detail.caseCode ?? "-", variant: "neutral" },
        ]}
      />

      <section className="wx-sos-dsec">
        <h3>เหตุการณ์ SOS ล่าสุด</h3>
        {latestEvent ? (
          <>
            <div className="wx-sos-kv-row">
              <span>รหัส SOS</span>
              <strong className="wx-sos-code">{latestEvent.sosCode ?? "-"}</strong>
            </div>
            <div className="wx-sos-kv-row">
              <span>ประเภท SOS</span>
              <strong>
                <span className="wx-badge wx-badge--info">{latestEvent.sosType ?? "-"}</span>
              </strong>
            </div>
            {latestEvent.message && (
              <div className="wx-sos-kv-row">
                <span>message</span>
                <strong className="wx-sos-message">{latestEvent.message}</strong>
              </div>
            )}
            <div className="wx-sos-kv-row">
              <span>เวลาจากเครื่อง</span>
              <strong>{formatDateTimeTh(latestEvent.clientTimestampUtc) ?? "-"}</strong>
            </div>
            <div className="wx-sos-kv-row">
              <span>ระบบรับเมื่อ</span>
              <strong>{formatDateTimeTh(latestEvent.serverReceivedAtUtc) ?? "-"}</strong>
            </div>
          </>
        ) : (
          <p className="wx-sos-note-muted wx-sos-note-muted--left">ยังไม่มีเหตุการณ์ SOS สำหรับเคสนี้</p>
        )}
      </section>

      <section className="wx-sos-dsec">
        <h3>ห่วงโซ่ความรับผิดชอบ</h3>
        <div className="wx-sos-kv-row">
          <span>แรงงาน</span>
          <strong>
            {detail.workerName ?? "-"} · {detail.workerCode ?? "-"}
          </strong>
        </div>
        <div className="wx-sos-kv-row">
          <span>Assignment</span>
          <strong className="wx-sos-code">{detail.assignmentCode ?? detail.assignmentId ?? "-"}</strong>
        </div>
        <div className="wx-sos-kv-row">
          <span>นายจ้าง</span>
          <strong>{detail.employerName ?? "-"}</strong>
        </div>
        <div className="wx-sos-kv-row">
          <span>Agent</span>
          <strong>
            {detail.agentName ?? "-"} · {detail.agentCode ?? "-"}
          </strong>
        </div>
      </section>

      <section className="wx-sos-dsec">
        <h3>Event Timeline ({timeline.length})</h3>
        {timeline.length === 0 ? (
          <p className="wx-sos-note-muted wx-sos-note-muted--left">ยังไม่มีเหตุการณ์ในไทม์ไลน์ของเคสนี้</p>
        ) : (
          <div className="wx-sos-timeline-list">
            {timeline.map((entry) => (
              <div className="wx-sos-timeline-row" key={entry.sosEventId}>
                <div className="wx-sos-timeline-row-top">
                  <strong>{entry.sosCode ?? entry.sosEventId}</strong>
                  <span>{formatDateTimeTh(entry.serverReceivedAtUtc) ?? "-"}</span>
                </div>
                {entry.message && (
                  <p>
                    {entry.sosType ?? "-"} · {entry.message}
                  </p>
                )}
                <span className="wx-sos-timeline-meta">
                  location: {entry.locationSource ?? "-"}
                  {formatCoordinates(entry.latitude, entry.longitude) ? ` · ${formatCoordinates(entry.latitude, entry.longitude)}` : ""}
                </span>
              </div>
            ))}
          </div>
        )}
        <p className="wx-sos-note-muted wx-sos-note-muted--left">{LINKED_CASE_NOTE}</p>
      </section>

      <section className="wx-sos-dsec">
        <h3>ตำแหน่งล่าสุด (Location Snapshot)</h3>
        <div className="wx-sos-kv-row">
          <span>ที่มาของพิกัด</span>
          <strong>
            <span className="wx-badge wx-badge--neutral">{detail.locationSource ?? "-"}</span>
          </strong>
        </div>
        <div className="wx-sos-kv-row">
          <span>latitude / longitude</span>
          <strong>{formatCoordinates(detail.latitude, detail.longitude) ?? "-"}</strong>
        </div>
        <div className="wx-sos-kv-row">
          <span>ความแม่นยำ (เมตร)</span>
          <strong>{detail.accuracyMeters ?? "-"}</strong>
        </div>
        <p className="wx-sos-note-muted wx-sos-note-muted--left">{LOCATION_SNAPSHOT_NOTE}</p>
      </section>

      <section className="wx-sos-dsec">
        <h3>Recipients ที่เตรียมไว้ ({recipients.length})</h3>
        {recipients.length === 0 ? (
          <p className="wx-sos-note-muted wx-sos-note-muted--left">ยังไม่มี recipient ที่เตรียมไว้สำหรับเคสนี้</p>
        ) : (
          <div className="wx-sos-recipient-list">
            {recipients.map((recipient) => (
              <div className="wx-sos-recipient-row" key={recipient.recipientId}>
                <span className={`wx-badge wx-badge--${recipient.recipientType === "Agent" ? "info" : "neutral"}`}>{recipient.recipientType}</span>
                <span className="wx-sos-recipient-name">{recipient.recipientAgentId ?? recipient.recipientWorkerId ?? recipient.recipientUserId ?? "-"}</span>
                <span className="wx-badge wx-badge--neutral">{recipient.deliveryStatus}</span>
              </div>
            ))}
          </div>
        )}
        <div className="wx-sos-notice">ระบบยังไม่แจ้งให้อัตโนมัติ กรุณาติดต่อแรงงานหรือหัวหน้างานเอง</div>
        <p className="wx-sos-note-muted wx-sos-note-muted--left">{NO_SEND_BUTTON_NOTE}</p>
      </section>

      <section className="wx-sos-dsec">
        <h3>ขั้นตอนถัดไป (Next Actions)</h3>
        <div className="wx-sos-next-actions">
          <button type="button" className="wx-sos-row-action wx-sos-row-action--full" disabled={!nextActions.canAcknowledge} onClick={onAcknowledge}>
            รับทราบ (Acknowledge)
          </button>
          <button type="button" className="wx-sos-row-action wx-sos-row-action--full" disabled={!nextActions.canResolve} onClick={onResolve}>
            ปิดเคส (Resolve)
          </button>
          {nextActions.canViewFootstepHistory && detail.workerId ? (
            <Link to="/backoffice/tracking/footsteps" className="wx-sos-row-action wx-sos-row-action--full wx-sos-row-action--ghost">
              ดูประวัติ Footstep (อ้างอิง)
            </Link>
          ) : (
            <span className="wx-sos-row-action wx-sos-row-action--full wx-sos-row-action--ghost wx-sos-row-action--disabled">
              ดูประวัติ Footstep (อ้างอิง)
            </span>
          )}
        </div>
        <p className="wx-sos-note-muted wx-sos-note-muted--left">
          canAcknowledge/canResolve คำนวณจาก role และ CaseStatus จริงของผู้ใช้ที่ล็อกอิน · canViewFootstepHistory เป็น true เสมอเพราะ Footstep History
          (P114/115) มีอยู่จริงแล้ว
        </p>
      </section>
    </div>
  );
}

function AcknowledgeDrawerView({ titleId, detail, note, onNoteChange, submitState, onSubmit, onBack }) {
  const submitting = submitState.status === "submitting";

  return (
    <div className="wx-sos-drawer">
      <span className="wx-sos-drawer-kicker">ACKNOWLEDGE</span>
      <h2 id={titleId} className="wx-sos-drawer-title">
        รับทราบเคส SOS
      </h2>
      <p className="wx-sos-drawer-sub">
        {detail?.caseCode ?? "-"} · {detail?.workerName ?? "-"}
      </p>
      <div className="wx-sos-drawer-status">
        <span className="wx-badge wx-badge--warning">Open -&gt; Acknowledged</span>
      </div>

      <section className="wx-sos-dsec">
        <h3>รายละเอียด</h3>
        <label className="wx-sos-field">
          <span>
            note <span className="wx-sos-optional">(ไม่บังคับ)</span>
          </span>
          <textarea
            aria-label="note"
            value={note}
            onChange={(event) => onNoteChange(event.target.value)}
            placeholder="เช่น ติดต่อ Agent แล้ว กำลังเดินทางไปยังจุดเกิดเหตุ"
          />
        </label>
      </section>

      <div className="wx-sos-notice">
        {ACKNOWLEDGE_NOTICE_LINES.map((line) => (
          <span key={line} className="wx-sos-notice-line">
            {line}
          </span>
        ))}
      </div>

      {submitState.status === "error" && (
        <p className="wx-sos-form-message" role="alert">
          {describeWriteError(submitState.error, "เคสนี้ถูกปิดหรือรับทราบแล้ว ไม่สามารถ acknowledge ซ้ำได้")}
        </p>
      )}

      <section className="wx-sos-dsec">
        <h3>รับเรื่องเคสนี้</h3>
      </section>

      <div className="wx-sos-drawer-actions">
        <button type="button" className="wx-button wx-button--ghost" onClick={onBack} disabled={submitting}>
          ย้อนกลับ
        </button>
        <button type="button" className="wx-button wx-button--primary" onClick={onSubmit} disabled={submitting}>
          {submitting ? "กำลังบันทึก..." : "ยืนยัน Acknowledge"}
        </button>
      </div>
    </div>
  );
}

function ResolveDrawerView({ titleId, detail, resolutionNote, onResolutionNoteChange, validationError, submitState, onSubmit, onBack }) {
  const submitting = submitState.status === "submitting";

  return (
    <div className="wx-sos-drawer">
      <span className="wx-sos-drawer-kicker">RESOLVE</span>
      <h2 id={titleId} className="wx-sos-drawer-title">
        ปิดเคส SOS (Resolve)
      </h2>
      <p className="wx-sos-drawer-sub">
        {detail?.caseCode ?? "-"} · {detail?.workerName ?? "-"}
      </p>
      <div className="wx-sos-drawer-status">
        <span className="wx-badge wx-badge--warning">{detail?.caseStatus ?? "Open"} -&gt; Resolved</span>
      </div>

      <section className="wx-sos-dsec">
        <h3>รายละเอียด</h3>
        <label className="wx-sos-field">
          <span>
            หมายเหตุการปิดเรื่อง <b>*</b>
          </span>
          <textarea
            aria-label="หมายเหตุการปิดเรื่อง"
            value={resolutionNote}
            onChange={(event) => onResolutionNoteChange(event.target.value)}
            placeholder="เช่น ติดต่อแรงงานทางโทรศัพท์แล้ว ยืนยันปลอดภัย เหตุการณ์คลี่คลายแล้ว"
          />
        </label>
      </section>

      {validationError && (
        <p className="wx-sos-form-message" role="alert">
          {validationError}
        </p>
      )}

      <div className="wx-sos-notice wx-sos-notice--warn">
        {RESOLVE_WARNING_LINES.map((line) => (
          <span key={line} className="wx-sos-notice-line">
            {line}
          </span>
        ))}
      </div>

      {submitState.status === "error" && (
        <p className="wx-sos-form-message" role="alert">
          {describeWriteError(submitState.error, "เคสนี้ถูกปิดไปแล้ว ไม่สามารถ resolve ซ้ำได้")}
        </p>
      )}

      <section className="wx-sos-dsec">
        <h3>ปิดเคสนี้</h3>
      </section>

      <div className="wx-sos-drawer-actions">
        <button type="button" className="wx-button wx-button--ghost" onClick={onBack} disabled={submitting}>
          ย้อนกลับ
        </button>
        <button type="button" className="wx-button wx-button--primary" onClick={onSubmit} disabled={submitting}>
          {submitting ? "กำลังบันทึก..." : "ยืนยัน Resolve"}
        </button>
      </div>
    </div>
  );
}
